import { useEffect, useCallback } from 'react'
import { useWebSocket } from './WebSocketProvider'
import { TASK_EVENTS } from './constants'

const useTaskEvents = (options = {}) => {
  const { subscribe } = useWebSocket()

  const {
    onCreated,
    onUpdated,
    onCompleted,
    onFailed,
    onPending,
    onRunning,
    serviceId,
    projectId,
    nodeId,
  } = options

  const matchesFilter = useCallback((task) => {
    if (serviceId && task.dockerServiceId !== serviceId) return false
    if (projectId && task.dockerProjectId !== projectId) return false
    if (nodeId && task.nodeId !== nodeId) return false
    return true
  }, [serviceId, projectId, nodeId])

  const createHandler = useCallback((handler) => {
    if (!handler) return null
    return (task) => {
      if (matchesFilter(task)) {
        handler(task)
      }
    }
  }, [matchesFilter])

  useEffect(() => {
    const unsubscribers = []

    if (onCreated) {
      const handler = createHandler(onCreated)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.CREATED, handler))
      }
    }

    if (onUpdated) {
      const handler = createHandler(onUpdated)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.UPDATED, handler))
      }
    }

    if (onCompleted) {
      const handler = createHandler(onCompleted)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.COMPLETED, handler))
      }
    }

    if (onFailed) {
      const handler = createHandler(onFailed)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.FAILED, handler))
      }
    }

    if (onPending) {
      const handler = createHandler(onPending)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.PENDING, handler))
      }
    }

    if (onRunning) {
      const handler = createHandler(onRunning)
      if (handler) {
        unsubscribers.push(subscribe(TASK_EVENTS.RUNNING, handler))
      }
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe())
    }
  }, [subscribe, createHandler, onCreated, onUpdated, onCompleted, onFailed, onPending, onRunning])
}

export default useTaskEvents
